/**
 * Creator Studio — Exports Page
 *
 * View and manage exported asset packets (background JPEG + .js overlay files).
 * This is the dual-output system for ADA compliance.
 */

import { useEffect } from 'react';
import {
  Download,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  FileImage,
  FileCode2,
} from 'lucide-react';
import { useStudioStore } from '../stores/studioStore';
import type { ExportStatus } from '../types';

const STATUS_CONFIG: Record<ExportStatus, { icon: typeof CheckCircle2; color: string; label: string }> = {
  pending:    { icon: Clock,         color: 'text-zinc-400',    label: 'Pending' },
  processing: { icon: Loader2,       color: 'text-amber-400',   label: 'Processing' },
  completed:  { icon: CheckCircle2,  color: 'text-emerald-400', label: 'Completed' },
  failed:     { icon: AlertTriangle, color: 'text-red-400',     label: 'Failed' },
};

export default function ExportsPage() {
  const { exportJobs, exportsLoading, loadExportJobs } = useStudioStore();

  useEffect(() => {
    loadExportJobs();
  }, [loadExportJobs]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">

        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
            <Download size={20} className="text-cyan-400" />
            Exports
          </h1>
          <p className="text-sm text-zinc-500">
            Dual-output asset packets — background images + ADA-compliant text overlay files
          </p>
        </div>

        <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
              <CheckCircle2 size={16} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-emerald-300">ADA-Compliant Dual Output</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Every export generates two files per school: a <strong className="text-zinc-300">background image</strong> (visual elements only, no baked-in text) and a <strong className="text-zinc-300">.js overlay file</strong> (HTML/CSS with real, selectable, screen-reader-accessible text positioned over the background).
              </p>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <FileImage size={12} className="text-amber-400" />
                  Background JPEG
                </span>
                <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <FileCode2 size={12} className="text-blue-400" />
                  .js Overlay File
                </span>
              </div>
            </div>
          </div>
        </div>

        {exportsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-zinc-900/50 border border-white/[0.04] animate-pulse" />
            ))}
          </div>
        ) : exportJobs.length > 0 ? (
          <div className="space-y-3">
            {exportJobs.map((job) => {
              const config = STATUS_CONFIG[job.status];
              const StatusIcon = config.icon;

              return (
                <div
                  key={job.id}
                  className="p-4 rounded-xl bg-zinc-900/50 border border-white/[0.06] hover:border-white/[0.10] transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <StatusIcon
                        size={18}
                        className={`${config.color} ${job.status === 'processing' ? 'animate-spin' : ''}`}
                      />
                      <div>
                        <h3 className="text-sm font-medium text-zinc-200">
                          Export Job
                        </h3>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          {job.total_schools} school{job.total_schools !== 1 ? 's' : ''} &middot; {job.image_format.toUpperCase()} &middot; Created {new Date(job.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                        job.status === 'completed'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : job.status === 'failed'
                          ? 'bg-red-500/10 text-red-400'
                          : job.status === 'processing'
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'bg-zinc-500/10 text-zinc-500'
                      }`}>
                        {config.label}
                      </span>
                    </div>
                  </div>

                  {job.status === 'processing' && job.total_schools > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                        <span>{job.completed_schools} of {job.total_schools} completed</span>
                        <span>{Math.round((job.completed_schools / job.total_schools) * 100)}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-purple-500 transition-all duration-500"
                          style={{ width: `${(job.completed_schools / job.total_schools) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 rounded-xl bg-zinc-900/30 border border-white/[0.04] text-center">
            <Download size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">No exports yet</p>
            <p className="text-xs text-zinc-600 mt-1">
              Exports are generated from published templates
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
